import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, ButtonGroup, Chip, CircularProgress, IconButton, Skeleton, Typography, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Link } from "@mui/material"
import styled from "@emotion/styled"
import { Container } from "@mui/system";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import { fetchAccountUsers } from "../redux/slices/users";
import { getSkills } from "../redux/slices/skill";
import ProjectAccountInfo from "../components/ProjectAccountInfo";
import ProjectBudgetTime from "../components/ProjectBudgetTime";
import ProjectStatusPriority from "../components/ProjectStatusPriority";
import ProjectTaskCard from "../components/ProjectTaskCard";
import ProjectPeople from "../components/ProjectPeople";
import ProjectContext from "../components/ProjectContext";
import ProjectTagContext from "../components/ProjectTagContext";
import AIResponseDialog from "../components/AIResponseDialog";
import PageHeader from "../components/PageHeader";
import { Check, Close, Edit, Delete, AutoAwesome, MenuBook } from "@mui/icons-material";
import { getProject, updateProject, updateProjectStatus, deleteProject, clearProject } from "../redux/slices/project";
import { triggerRefresh } from "../redux/slices/refreshTriggers";
import { getProjectAiSummary, requestProjectAiSummary } from "../redux/ducks/ai";
import { createKBArticleFromProject, requestKBAIRewrite } from "../redux/ducks/kb";
import { showAlert } from "../redux/slices/alerts";
import { useHasPermission } from "../utils/permissionUtils";
import { useIsProjectLocked } from "../utils/lockUtils";
import { useNavigate } from 'react-router-dom';
import { getSocket } from "../services/socketService";
import ProjectPayments from "../components/ProjectPayments";
import RetainerOverageNotice from "../components/RetainerOverageNotice";
import ProjectAuthorizationNotice from "../components/ProjectAuthorizationNotice";
import CompleteCascadeDialog from "../components/CompleteCascadeDialog";
import BillingImpactDialog from "../components/BillingImpactDialog";
import MoveProjectDialog from "../components/MoveProjectDialog";
import { useRetainerStatus } from "../hooks/useRetainerStatus";

const ContainerStyled = styled(Container)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    marginTop: theme.spacing(2),
}));

const GridContainer = styled('div')(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: theme.spacing(2),
}));

const SkeletonCard = styled(Skeleton)(({ theme }) => ({
    gridColumn: 'span 3',
    [theme.breakpoints.up('sm')]: {
        gridColumn: 'span 1',
    },
}));

const SkeletonCardWide = styled(Skeleton)(({ theme }) => ({
    gridColumn: 'span 3',
    [theme.breakpoints.up('sm')]: {
        gridColumn: 'span 2',
    },
}));

const SkeletonCardFull = styled(Skeleton)(({ theme }) => ({
    gridColumn: 'span 3',
}));

const SourceQuoteNotice = styled(Alert)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const Project = () => {
    const dispatch = useDispatch();
    const { projectId } = useParams();
    const accountId = useSelector(state => state.accountUsers.accountId);
    const project = useSelector(state => state.project.currentProject);
    const isProjectLoaded = project?._id === projectId;
    const [editedName, setEditedName] = useState('');
    const [isEditingName, setIsEditingName] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    const refreshTrigger = useSelector(state => state.refreshTriggers['project']);
    const skills = useSelector(state => state.skills);
    const hasPermission = useHasPermission()
    const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
    const [deletionImpact, setDeletionImpact] = useState(null);
    const [accountAfterDelete, setAccountAfterDelete] = useState(null);
    const canEditProjectName = hasPermission('editProjectName');
    const canCompleteProject = hasPermission('completeProject');
    const canDeleteProject = hasPermission('deleteProjects');
    const canEditCompletedProject = hasPermission('editCompletedProjects');
    const canSetProjectBilling = hasPermission('setProjectBilling');
    const canSeeProjectBilling = hasPermission('seeProjectBilling');
    const canSeeManagement = hasPermission('canSeeManagement');
    const canMoveProject = hasPermission('canMoveItems');
    const [moveDialogOpen, setMoveDialogOpen] = useState(false);
    const isDeleted = isProjectLoaded && !!project.isDeleted;
    const isLocked = useIsProjectLocked(project);
    const { status: retainerStatus } = useRetainerStatus({ accountId: project?.account?._id });
    const [projectToComplete, setProjectToComplete] = useState(null);
    const [isCompleting, setIsCompleting] = useState(false);
    const [kbDialogOpen, setKbDialogOpen] = useState(false);
    const [kbCreating, setKbCreating] = useState(false);
    const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
    const [summaryRecord, setSummaryRecord] = useState(null);
    const [summaryBusy, setSummaryBusy] = useState(false);
    const [summaryError, setSummaryError] = useState(null);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        dispatch(getProject({ projectId }));
    }, [projectId, refreshTrigger, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(clearProject());
        };
    }, [projectId, dispatch]);

    useEffect(() => {
        setIsCompleted(!!project.completedDate)
    }, [project.completedDate, dispatch])

    useEffect(() => {
        if (skills.length === 0) {
            dispatch(getSkills());
        }
    }, [skills, projectId, dispatch]);

    useEffect(() => {
        if (project) {
            setIsCompleted(!!project.completedDate);

            if (project.account && project.account._id) {
                if (accountId !== project.account._id) {
                    dispatch(fetchAccountUsers({ accountId: project.account._id }));
                }
            }
        }
    }, [project, accountId, dispatch]);

    const handleEditName = () => {
        setEditedName(project.name);
        setIsEditingName(true);
    }

    const handleNameChange = (event) => {
        setEditedName(event.target.value);
    };

    const handleCancelEditName = () => {
        setIsEditingName(false);
        setEditedName('');
    };

    const handleSaveName = () => {
        if (isLocked) return;
        dispatch(updateProject({ projectId, projectData: { name: editedName } }));
        setIsEditingName(false);
    };

    const handleDeleteProject = () => {
        if (isLocked) return;
        setConfirmDeleteDialogOpen(true);
    };

    const confirmDeleteProject = () => {
        if (isLocked) return;
        const account = project.account.slug;
        setConfirmDeleteDialogOpen(false);
        setAccountAfterDelete(account);
        dispatch(deleteProject({
            projectId,
            callback: (response) => {
                if (response?.billingImpactSummary) {
                    setDeletionImpact({ impact: response.billingImpact, summary: response.billingImpactSummary });
                    return;
                }
                navigate(`/account/${account}`);
            }
        }));
    };

    const handleCompleteProject = () => {
        if (isLocked) return;
        setProjectToComplete({ type: 'project', id: projectId, name: project.name });
    };

    const handleReopenProject = () => {
        if (isLocked) return;
        dispatch(updateProjectStatus({ projectId, status: "In Progress" }));
    };

    const fetchSummaryState = useCallback(() => {
        if (!projectId) return Promise.resolve(null);
        return new Promise((resolve) => {
            dispatch(getProjectAiSummary(projectId, (resp) => {
                if (resp?.ok) {
                    const record = resp.data?.exists ? resp.data.record : null;
                    setSummaryRecord(record);
                    setSummaryError(null);
                    resolve(record);
                } else {
                    setSummaryError(resp?.message || "Failed to read AI summary state");
                    resolve(null);
                }
            }));
        });
    }, [projectId, dispatch]);

    const requestSummary = useCallback(() => {
        if (!projectId) return Promise.resolve();
        setSummaryBusy(true);
        setSummaryError(null);
        return new Promise((resolve) => {
            dispatch(requestProjectAiSummary(projectId, (resp) => {
                if (resp?.ok) {
                    setSummaryRecord(resp.data?.record || null);
                } else {
                    setSummaryError(resp?.message || "Failed to request AI summary");
                }
                setSummaryBusy(false);
                resolve();
            }));
        });
    }, [projectId, dispatch]);

    const openSummaryDialog = useCallback(async () => {
        if (!projectId || summaryBusy) return;
        setSummaryDialogOpen(true);
        setSummaryBusy(true);
        const existing = await fetchSummaryState();
        setSummaryBusy(false);
        if (!existing) {
            await requestSummary();
        }
    }, [projectId, summaryBusy, fetchSummaryState, requestSummary]);

    const closeSummaryDialog = () => {
        setSummaryDialogOpen(false);
        setSummaryRecord(null);
        setSummaryError(null);
        if (searchParams.get('aiSummary')) {
            const next = new URLSearchParams(searchParams);
            next.delete('aiSummary');
            setSearchParams(next, { replace: true });
        }
    };

    const autoOpenedRef = useRef(false);
    useEffect(() => {
        if (autoOpenedRef.current) return;
        if (!canSeeManagement) return;
        if (searchParams.get('aiSummary') !== '1') return;
        autoOpenedRef.current = true;
        openSummaryDialog();
    }, [canSeeManagement, searchParams, openSummaryDialog]);

    useEffect(() => {
        if (!summaryDialogOpen) return;
        const socket = getSocket();
        if (!socket) return;
        const handler = (payload) => {
            if (payload?.kind !== 'project-summary') return;
            if (payload?.contextId !== projectId) return;
            fetchSummaryState();
        };
        socket.on('aiResponse:ready', handler);
        return () => { socket.off('aiResponse:ready', handler); };
    }, [summaryDialogOpen, projectId, fetchSummaryState]);

    return (
        <ContainerStyled>
            <PageHeader
                title={
                    !isEditingName
                        ? (isProjectLoaded ? project.name : <Skeleton width={280} />)
                        : (
                            <TextField
                                autoFocus
                                fullWidth
                                label="Project Name"
                                value={editedName}
                                onChange={handleNameChange}
                                variant="outlined"
                                size="small"
                            />
                        )
                }
                subtitle={isProjectLoaded && project.projectId ? `Project #${project.projectId}` : null}
                titleActions={
                    <>
                        {isDeleted && <Chip size="small" color="error" label="Deleted" />}
                        {!isEditingName && isProjectLoaded && (!isCompleted || canEditCompletedProject) && canEditProjectName && !isLocked && (
                            <IconButton onClick={handleEditName} aria-label="Edit project name">
                                <Edit />
                            </IconButton>
                        )}
                        {!isEditingName && isProjectLoaded && !isCompleted && canDeleteProject && !isLocked && (
                            <IconButton onClick={handleDeleteProject} aria-label="Delete project">
                                <Delete />
                            </IconButton>
                        )}
                        {isEditingName && (
                            <ButtonGroup>
                                {!isLocked && (
                                    <IconButton onClick={handleSaveName} aria-label="Save project name">
                                        <Check />
                                    </IconButton>
                                )}
                                {!isLocked && (
                                    <IconButton onClick={handleCancelEditName} aria-label="Cancel rename">
                                        <Close />
                                    </IconButton>
                                )}
                            </ButtonGroup>
                        )}
                    </>
                }
                actions={
                    <>
                        {!isEditingName && isProjectLoaded && hasPermission('canSeeInternal') && (
                            <Button
                                variant="outlined"
                                onClick={() => setKbDialogOpen(true)}
                                disabled={kbCreating}
                                startIcon={<MenuBook />}
                            >
                                KB Article
                            </Button>
                        )}
                        {!isEditingName && isProjectLoaded && canSeeManagement && (
                            <Button
                                variant="outlined"
                                onClick={openSummaryDialog}
                                disabled={summaryBusy && !summaryDialogOpen}
                                startIcon={<AutoAwesome />}
                            >
                                AI Summary
                            </Button>
                        )}
                        {!isEditingName && isProjectLoaded && canCompleteProject && project.status !== "Awaiting Authorization" && (!isCompleted ? (
                            <Button
                                variant="contained"
                                onClick={handleCompleteProject}
                                disabled={isLocked || isCompleting}
                                startIcon={isCompleting ? <CircularProgress size={16} color="inherit" /> : null}
                            >
                                {isCompleting ? "Completing…" : "Complete Project"}
                            </Button>
                        ) : (
                            <Button variant="contained" onClick={handleReopenProject} disabled={isLocked}>Reopen Project</Button>
                        ))}
                    </>
                }
            />

            {isProjectLoaded ? (
                <>
                    <RetainerOverageNotice status={retainerStatus} />
                    <ProjectAuthorizationNotice />
                    {project.sourceQuote && hasPermission('canManageQuotes') && (
                        <SourceQuoteNotice severity="info">
                            Project created from quote &rarr;{" "}
                            <Link component="button" onClick={() => navigate(`/quotes/${project.sourceQuote._id || project.sourceQuote}`)}>
                                {project.sourceQuote.name || "View quote"}
                            </Link>
                            {project.sourceQuote.status ? ` (${project.sourceQuote.status})` : ""}
                        </SourceQuoteNotice>
                    )}

                    <GridContainer>
                        <ProjectAccountInfo
                            canMove={canMoveProject && !isLocked && (!isCompleted || canEditCompletedProject)}
                            onMove={() => setMoveDialogOpen(true)}
                        />
                        <ProjectBudgetTime />
                        <ProjectStatusPriority />
                        {(canSetProjectBilling || canSeeProjectBilling) && (project?.billingTerms?.type === "Fixed Price" || project?.group?.billingTerms?.type === "Fixed Price" || project?.payments?.length > 0 || project?.group?.payments?.length > 0) && <ProjectPayments type={project?.group?.billingTerms?.type === "Fixed Price" || project?.group?.payments?.length > 0 ? "Group" : "Project"} />}
                        <ProjectTaskCard tasks={project?.tasks || []} projectId={projectId} accountId={project?.account?._id} />
                        <ProjectPeople />
                        {hasPermission('canSeeInternal') && <ProjectTagContext />}
                        <ProjectContext />
                    </GridContainer>
                </>
            ) : (
                <GridContainer>
                    <SkeletonCard variant="rounded" height={190} />
                    <SkeletonCard variant="rounded" height={190} />
                    <SkeletonCard variant="rounded" height={190} />
                    <SkeletonCardWide variant="rounded" height={260} />
                    <SkeletonCard variant="rounded" height={260} />
                    <SkeletonCardFull variant="rounded" height={320} />
                </GridContainer>
            )}

            <Dialog open={confirmDeleteDialogOpen} onClose={() => setConfirmDeleteDialogOpen(false)}>
                <DialogTitle>Delete Project</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this project?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={confirmDeleteProject} variant="contained" color="primary">Delete</Button>
                </DialogActions>
            </Dialog>

            <AIResponseDialog
                open={summaryDialogOpen}
                onClose={closeSummaryDialog}
                title="Project AI Summary"
                record={summaryRecord}
                busy={summaryBusy}
                error={summaryError}
                onRetry={requestSummary}
            />

            <BillingImpactDialog
                open={!!deletionImpact}
                impact={deletionImpact?.impact}
                summary={deletionImpact?.summary}
                closeLabel="Back to the account"
                onClose={() => {
                    setDeletionImpact(null);
                    if (accountAfterDelete) navigate(`/account/${accountAfterDelete}`);
                }}
            />

            <MoveProjectDialog
                open={moveDialogOpen}
                onClose={() => setMoveDialogOpen(false)}
                project={project}
                onMoved={(response) => {
                    setMoveDialogOpen(false);
                    if (response?.accountSlug) {
                        navigate(`/account/${response.accountSlug}/project/${projectId}`, { replace: true });
                    }
                    dispatch(getProject({ projectId }));
                }}
            />

            <CompleteCascadeDialog
                target={projectToComplete}
                confirm={false}
                onRunningChange={setIsCompleting}
                onClose={() => setProjectToComplete(null)}
                onCompleted={() => dispatch(triggerRefresh({ triggerName: 'project' }))}
            />

            <CreateKBFromProjectDialog
                open={kbDialogOpen}
                projectId={projectId}
                projectName={project?.name}
                creating={kbCreating}
                onClose={() => setKbDialogOpen(false)}
                onSubmit={(useAI) => {
                    setKbCreating(true);
                    if (useAI) {
                        dispatch(requestKBAIRewrite(projectId, {}, (resp) => {
                            setKbCreating(false);
                            setKbDialogOpen(false);
                            if (resp) {
                                dispatch(showAlert({ severity: 'success', message: 'AI rewrite requested. You\'ll be notified when it\'s ready.' }));
                            }
                        }));
                    } else {
                        dispatch(createKBArticleFromProject(projectId, {}, (article) => {
                            setKbCreating(false);
                            setKbDialogOpen(false);
                            if (article) {
                                dispatch(showAlert({ severity: 'success', message: 'KB article created' }));
                                navigate(`/kb/${article._id}`);
                            }
                        }));
                    }
                }}
            />

        </ContainerStyled>
    )
}

const CreateKBFromProjectDialog = ({ open, projectName, creating, onClose, onSubmit }) => (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Create KB Article</DialogTitle>
        <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
                Create a Knowledge Base article from <strong>{projectName}</strong>.
            </Typography>
            <Typography variant="body2" color="text.secondary">
                <strong>Import</strong> copies the project title and description as-is.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>AI Rewrite</strong> reads the project's tasks and comments to generate a polished article. You'll be notified when it's ready.
            </Typography>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose} disabled={creating}>Cancel</Button>
            <Button
                variant="outlined"
                onClick={() => onSubmit(true)}
                disabled={creating}
                startIcon={creating ? <CircularProgress size={16} /> : <AutoAwesome />}
            >
                AI Rewrite
            </Button>
            <Button
                variant="contained"
                onClick={() => onSubmit(false)}
                disabled={creating}
                startIcon={creating ? <CircularProgress size={16} /> : null}
            >
                Import
            </Button>
        </DialogActions>
    </Dialog>
);

export default Project;